import useAxios from '../hooks/useAxios';
import {
  DERMA_DIAGNOSE_URL,
  DERMA_IMAGE_URLS_URL,
  DERMA_NEXT_QUESTIONS_URL,
  DERMA_IMAGE_VALIDITY_URL,
} from './constants';

/**
 * Hook for interacting with the Dermadetect API.
 *
 * @returns {Object} An object containing functions to interact with the API.
 */
export const useDermaApi = () => {
  const api = useAxios();

  /**
   * Posts the next set of questions for dermadetect diagnosis.
   *
   * @param {Array} questions - An array of questions for the diagnosis.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postNextQuestions = questions =>
    api.post(DERMA_NEXT_QUESTIONS_URL, {
      questions,
    });

  /**
   * Posts image URLs for dermadetect diagnosis.
   *
   * @param {FormData} formData - The FormData object containing image URLs.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postImageUrls = formData =>
    api.post(DERMA_IMAGE_URLS_URL, formData, {
      headers: {'Content-Type': 'multipart/form-data'},
    });

  /**
   * Posts a dermadetect diagnosis based on anamnesis body data.
   *
   * @param {Object} anamBody - Anamnesis body data for dermadetect diagnosis.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postDiagnose = anamBody => api.post(DERMA_DIAGNOSE_URL, {anam_body: anamBody});

  /**
   * Posts the validity of an image URL for dermadetect diagnosis.
   *
   * @param {string} imageURL - The URL of the image to validate.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postImageValidity = imageURL => api.post(DERMA_IMAGE_VALIDITY_URL, {path: imageURL});

  return {
    postNextQuestions,
    postImageUrls,
    postDiagnose,
    postImageValidity,
  };
};
